<template>
  <SrpModal v-model:isVisible="_isVisible" size="medium">
    <template #content>
      <div class="edit-title-and-comment-form">
        <div class="standard-page-header standard-page-header--small-bottom-padding edit-title-and-comment-form__header">
          <h1 class="global-h1 standard-page-header__title">
            {{ props.photoGalleryId ? "Edit Group Title and Comment" : "Create a New Group" }}
          </h1>
        </div>

        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <h4 class="global-h4 title-and-form-element__title">Title</h4>
          <input class="global-text-input title-and-form-element__form-element" type="text" style="max-width: 390px" v-model="_title" :disabled="isSaving" placeholder="Summer Festival Highlights" />
        </div>

        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <h4 class="global-h4 title-and-form-element__title">Business or Location <span style="font-weight: 500; color: rgba(0, 0, 0, 0.5)">(optional)</span></h4>
          <AssignLocationField v-if="props.customerId" :customerId="props.customerId" v-model:locationId="_locationId" :isDisabled="isSaving" :autoOpen="props.shouldOpenWithLocationPicker" />
        </div>

        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <h4 class="global-h4 title-and-form-element__title">Description / Notes <span style="font-weight: 500; color: rgba(0, 0, 0, 0.5)">(optional)</span></h4>
          <textarea
            class="global-textarea title-and-form-element__form-element"
            style="height: 110px; min-height: 110px"
            v-model="_comment"
            :disabled="isSaving"
            placeholder="Location or event name, theme, etc."
          />
        </div>

        <div class="title-and-form-element edit-title-and-comment-form__title-and-form-element">
          <h4 class="global-h4 title-and-form-element__title">Photo Credit <span style="font-weight: 500; color: rgba(0, 0, 0, 0.5)">(optional)</span></h4>
          <input class="global-text-input title-and-form-element__form-element" type="text" style="max-width: 390px" v-model="_photoCredit" :disabled="isSaving" />
        </div>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <SrpButton color="gray" fill="outlined" style="margin-right: 12px" @click="closeModal" :isDisabled="isSaving">Cancel</SrpButton>

      <SrpButton v-if="props.photoGalleryId" @click="saveAndCloseModal(closeModal)" :isDisabled="isSaving || !_title">
        <template #icon>
          <LoadingIcon v-if="isSaving" :size="22" />
          <IconEmbedded v-else name="save_2" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Save
      </SrpButton>

      <SrpButton v-else @click="saveAndCloseModal(closeModal)" :isDisabled="isSaving || !_title">
        <template v-if="isSaving" #icon><LoadingIcon :size="22" /></template>
        Create
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import PhotoGalleryV2Repo from "@repos/PhotoGalleryV2Repo";

import SrpModal from "@components/ui/SrpModal.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import AssignLocationField from "@components/SocialLocations/AssignLocationField.vue";

import { PhotoGallerySource, PhotoGalleryCustomerData } from "@contracts/photosPageData";

const props = withDefaults(
  defineProps<{
    title?: string | null;
    comment?: string | null;
    photoCredit?: string | null;
    customerId: string | null;
    photoGalleryId?: string | null;
    currentOperationSource?: PhotoGallerySource;
    assignedLocationId?: string | null;
    shouldOpenWithLocationPicker?: boolean;
  }>(),
  {
    title: null,
    comment: null,
    photoCredit: null,
    customerId: null,
    photoGalleryId: null,
    currentOperationSource: PhotoGallerySource.ContentLibraryManual,
    assignedLocationId: null,
    shouldOpenWithLocationPicker: false,
  }
);

const emit = defineEmits<{
  (e: "save", upsertedPhotoGallery: PhotoGalleryCustomerData): void;
}>();

const _isVisible = defineModel<boolean>("isVisible", { required: true });
const _title = ref<string>("");
const _comment = ref<string>("");
const _photoCredit = ref<string>("");
const _locationId = ref<string | null>(null);
const isSaving = ref<boolean>(false);

onMounted(() => {
  _title.value = props.title;
  _comment.value = props.comment || "";
  _photoCredit.value = props.photoCredit || "";
  _locationId.value = props.assignedLocationId;
});

async function saveAndCloseModal(closeModalCallback: () => void) {
  const success = await save();
  if (!success) {
    return;
  }
  closeModalCallback?.();
}

async function save() {
  isSaving.value = true;

  let upsertedPhotoGallery: PhotoGalleryCustomerData = null;

  if (props.photoGalleryId) {
    upsertedPhotoGallery = await PhotoGalleryV2Repo.updateMyUploadsGroupCommentAndTitle(
      props.customerId,
      props.photoGalleryId,
      _title.value,
      _comment.value,
      props.currentOperationSource,
      _photoCredit.value,
      _locationId.value
    );
  } else {
    upsertedPhotoGallery = await PhotoGalleryV2Repo.createMyUploadsGroup(
      props.customerId,
      _title.value,
      _comment.value,
      props.currentOperationSource,
      _photoCredit.value,
      _locationId.value
    );
  }

  isSaving.value = false;
  emit("save", upsertedPhotoGallery);
  return true;
}
</script>

<style scoped lang="scss">
.title-and-form-element {
  &__title {
    margin-bottom: 7px;
  }
}

.edit-title-and-comment-form {
  &__header {
    margin-bottom: 18px;
  }

  &__title-and-form-element {
    margin-bottom: 28px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
